import axios from 'axios'
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios'

import {
  clearAccessToken,
  getAccessToken as readAccessTokenFromStorage,
  setAccessToken,
} from '@/lib/auth/token-storage'
import { recordApiCall, pathToRoute } from '@/services/telemetry'
import { normalizeApiError, type ApiError } from '@/lib/api-errors'

// Production builds MUST set VITE_API_URL — defaulting to a demo IP would
// silently route real users to a non-production server if the env var is
// forgotten in CI/CD. In dev we fall back to the localhost backend so
// `pnpm dev` Just Works without a .env, but we never paper over a missing
// env var in production.
function resolveApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL
  if (envUrl && typeof envUrl === 'string' && envUrl.length > 0) return envUrl
  if (import.meta.env.DEV) return 'http://localhost:5000/api'
  throw new Error(
    'VITE_API_URL is not defined. The production build refuses to fall back ' +
      'to a default URL — set VITE_API_URL in the deployment environment.',
  )
}

const API_BASE_URL = resolveApiBaseUrl()

if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.log('[API] Base URL:', API_BASE_URL)
}

// 15s is the highest you want a user-facing request to hang before showing
// an error. Past 15s the browser's 6-connections-per-origin limit starves;
// past 30s users assume the page is broken. Long-running ops (uploads,
// reports) override per-call.
const API_TIMEOUT = 15_000

// Lazy-loaded auth callbacks to avoid a circular dependency between this
// module and `AuthContext`. The access token itself now lives in
// `token-storage.ts` — it does not flow through these callbacks anymore.
let getAccessTokenCallback: (() => string | null) | null = null
let onTokenRefreshed: ((token: string) => void) | null = null
let onAuthFailed: (() => void) | null = null

/**
 * Initialize axios auth integration.
 * Called once from AuthProvider after context is ready.
 */
export function initializeAxiosAuth(config: {
  getAccessToken: () => string | null
  onTokenRefreshed: (token: string) => void
  onAuthFailed: () => void
}) {
  getAccessTokenCallback = config.getAccessToken
  onTokenRefreshed = config.onTokenRefreshed
  onAuthFailed = config.onAuthFailed
}

// ==================== Axios Instance ====================

// `withCredentials: true` instructs the browser to:
//   1. Send the httpOnly refresh-token cookie on requests to the API origin
//      (so `POST /auth/refresh` can read it without us touching the value).
//   2. Honor `Set-Cookie` headers on responses (so login can set the cookie).
// Requires the API to respond with `Access-Control-Allow-Credentials: true`
// AND a specific (not '*') `Access-Control-Allow-Origin` — both are set by
// the backend `cors()` middleware.
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': '69420',
  },
})

// ==================== Request Interceptor ====================

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // Order: AuthContext's live callback first (most up-to-date), then the
    // sessionStorage-backed cache as a fallback during the brief window on
    // app boot before the callback is registered.
    const token = getAccessTokenCallback?.() ?? readAccessTokenFromStorage()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Telemetry timestamp — read back in the response interceptor below.
    // Using metadata avoids polluting the request headers.
    ;(config as { metadata?: { startTime: number } }).metadata = { startTime: Date.now() }

    if (import.meta.env.DEV) {
      console.info(`[API] ${config.method?.toUpperCase()} ${config.url}`)
    }

    return config
  },
  (error: AxiosError) => Promise.reject(error)
)

// ==================== Centralized Token Refresh ====================

let isRefreshing = false
let refreshPromise: Promise<string> | null = null
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
  config: AxiosRequestConfig
}> = []

function processQueue(newToken: string | null) {
  failedQueue.forEach((req) => {
    if (newToken && req.config.headers) {
      req.config.headers.Authorization = `Bearer ${newToken}`
      req.resolve(api(req.config))
    } else {
      req.reject(new Error('Token refresh failed'))
    }
  })
  failedQueue = []
}

/**
 * Single entry point for refreshing the access token.
 *
 * The refresh token is NOT sent in the request body. The backend reads it
 * from the httpOnly `protea_rt` cookie, which the browser attaches
 * automatically because the axios instance has `withCredentials: true`.
 * On success the new access token is written to in-memory + sessionStorage
 * via `token-storage.ts` so subsequent requests in this tab pick it up.
 *
 * Deduplicates concurrent calls — only one refresh request is in-flight at
 * a time. Used by both the 401 interceptor and AuthContext session restore.
 *
 * `silent: true` suppresses the `onAuthFailed` callback on failure — used by
 * the cold-boot AuthContext restore path, where a missing/expired cookie is
 * the expected state for an anonymous visitor and the "Session expired"
 * toast would be wrong. The 401 interceptor still passes silent=false so a
 * mid-session expiry still surfaces the toast to the user.
 */
export function refreshAccessToken(opts?: { silent?: boolean }): Promise<string> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  const silent = opts?.silent === true

  isRefreshing = true
  refreshPromise = api
    // Empty body — the refresh token rides on the httpOnly cookie.
    .post('/auth/refresh', {})
    .then((response) => {
      const newAccessToken: string =
        response.data?.accessToken ?? response.data?.token
      if (!newAccessToken) {
        throw new Error('Refresh response missing accessToken')
      }
      setAccessToken(newAccessToken)
      onTokenRefreshed?.(newAccessToken)
      processQueue(newAccessToken)
      return newAccessToken
    })
    .catch((error) => {
      if (import.meta.env.DEV) {
        console.error(
          '[Auth] Token refresh failed:',
          error.response?.status,
          JSON.stringify(error.response?.data),
          'Has response:',
          !!error.response,
          'Error message:',
          error.message,
          'silent:',
          silent,
        )
      }
      processQueue(null)
      clearAccessToken()
      if (!silent) onAuthFailed?.()
      throw error
    })
    .finally(() => {
      isRefreshing = false
      refreshPromise = null
    })

  return refreshPromise
}

// ==================== Response Interceptor ====================

// Telemetry response interceptor — fire-and-forget per-request timing for
// the admin App Health page's "Slowest API calls" chart. Mirrors the mobile
// http_client wrap so web + mobile rows aggregate on the same endpoint
// template (`/matches/:id`). Skips /telemetry/* itself to prevent recursion
// (those run via fetch + keepalive, not through this interceptor).
api.interceptors.response.use(
  (response) => {
    try {
      const cfg = response.config as InternalAxiosRequestConfig & { metadata?: { startTime: number } }
      const started = cfg.metadata?.startTime
      const url = cfg.url ?? ''
      if (started && !url.startsWith('/telemetry')) {
        const method = (cfg.method ?? 'get').toUpperCase()
        const pathOnly = url.split('?')[0]
        recordApiCall({
          endpoint: `${method} ${pathToRoute(pathOnly)}`,
          durationMs: Date.now() - started,
          statusCode: response.status,
        })
      }
    } catch { /* swallow — telemetry must not break the response */ }
    return response
  },
  async (error: AxiosError) => {
    // Record timing on failed requests too so the chart includes 5xx
    // spikes / network drops — they're often the slow ones.
    try {
      const cfg = error.config as (InternalAxiosRequestConfig & { metadata?: { startTime: number } }) | undefined
      const started = cfg?.metadata?.startTime
      const url = cfg?.url ?? ''
      if (started && !url.startsWith('/telemetry')) {
        const method = (cfg?.method ?? 'get').toUpperCase()
        const pathOnly = url.split('?')[0]
        recordApiCall({
          endpoint: `${method} ${pathToRoute(pathOnly)}`,
          durationMs: Date.now() - started,
          statusCode: error.response?.status,
        })
      }
    } catch { /* swallow */ }

    // Attach a normalized error shape to every rejected response so callers
    // can read `err.normalized.message` / `err.normalized.kind` instead of
    // hand-extracting `err.response?.data?.error?.message`. Legacy callers
    // that still read `err.response.data.error.message` keep working — the
    // original axios error object is preserved, this just adds a property.
    try {
      ;(error as AxiosError & { normalized?: ApiError }).normalized = normalizeApiError(error)
    } catch { /* swallow — normalization must not break the response */ }

    // ──── original 401-refresh logic continues here ────
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

    // Only handle 401 with auto-refresh — skip auth endpoints (login, verify-otp, etc.)
    // because their 401s are business errors (role mismatch, invalid OTP), not token expiry.
    const isAuthEndpoint = originalRequest?.url?.match(
      /\/auth\/(login|verify-otp|register|admin\/login|admin\/verify-otp|sso|refresh|logout)/
    )
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true

      // If already refreshing, queue this request to retry after the refresh completes
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest })
        })
      }

      try {
        const newAccessToken = await refreshAccessToken()

        // Update original request header
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        }

        return api(originalRequest)
      } catch (refreshError) {
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default api
export { API_BASE_URL as API_URL }
